import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import type { TDerivedMetricDto } from '@homepal/api-contract'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Stack from 'components_new/atoms/Stack'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import GenericListItem from 'components_new/molecules/GenericListItem'
import SimpleTable from 'components_new/molecules/SimpleTable'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import {
  findCategoryName,
  findFactTable,
  formatExpression
} from 'components_new/organisms/internal/MetricStudio/utils'

import PageLayout from 'components_new/templates/PageLayout'

import { useDerivedMetricActions } from 'redux/actions/internal/DerivedMetrics'
import { useInternalDerivedMetrics } from 'redux/hooks/internal/DerivedMetrics'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'

/**
 * Mätetalsstudion — the derived metrics Homepal has defined.
 *
 * Every Homepal user can read the definitions; only the product team can
 * change them, and not in a preview, which mirrors them from production. The
 * actions are hidden rather than disabled for everyone else.
 */
const MetricStudioPage = () => {
  const { fetched, fetching, metrics, options, optionsFetched } =
    useInternalDerivedMetrics()

  const { isInProductTeam } = useAccountSettings()

  const canEdit = isInProductTeam && options.editable

  const {
    tryGetDerivedMetrics,
    tryGetDerivedMetricOptions,
    tryDeleteDerivedMetric
  } = useDerivedMetricActions()

  const history = useHistory()

  const [toDelete, setToDelete] = useState<TDerivedMetricDto | null>(null)

  useEffect(() => {
    if (!fetched) {
      tryGetDerivedMetrics()
    }
    if (!optionsFetched) {
      tryGetDerivedMetricOptions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const rows = metrics.map((metric) => {
    const factTable = findFactTable(options.fact_tables, metric.fact_table_id)

    return {
      id: metric.id,
      cells: [
        <GenericListItem
          key="metric"
          body={formatExpression(metric.expression, factTable)}
          BodyProps={{ noWrap: false }}
          component="div"
          disablePadding={true}
          title={metric.name}
        />,
        <Text key="category" variant="body2">
          {findCategoryName(options, metric.category)}
        </Text>,
        <Text key="fact-table" variant="body2">
          {metric.fact_table_name}
        </Text>,
        <Text key="unit" variant="body2">
          {metric.unit ?? '–'}
        </Text>,
        <Box key="in-use">
          {metric.in_use ? (
            <Chip color="success" label="Används" size="small" />
          ) : null}
        </Box>,
        <Stack
          key="actions"
          direction="row"
          sx={{ gap: 0.5, justifyContent: 'flex-end' }}
        >
          {/* The metric page computes the value for the selected customer,
              which is as close to a preview as it gets. */}
          <Tooltip title="Öppna mätetalet">
            <IconButton href={`/metrics/${metric.id}`}>
              <Icon name="NewTab" />
            </IconButton>
          </Tooltip>
          {canEdit ? (
            <>
              <Tooltip title="Ändra">
                <IconButton
                  onClick={() =>
                    history.push({
                      pathname: `/internal/metric-studio/${metric.id}`,
                      state: { edit: true }
                    })
                  }
                >
                  <Icon name="Edit" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  metric.in_use
                    ? 'Mätetalet används och kan inte tas bort'
                    : 'Ta bort'
                }
              >
                <IconButton
                  disabled={metric.in_use}
                  onClick={() => setToDelete(metric)}
                >
                  <Icon name="Delete" />
                </IconButton>
              </Tooltip>
            </>
          ) : null}
        </Stack>
      ]
    }
  })

  return (
    <PageLayout
      content={{ disablePadding: true, fullWidth: true }}
      topbar={{
        breadcrumbs: [{ title: 'Admin' }, { title: 'Mätetalsstudion' }],
        actions: canEdit ? (
          <Button
            onClick={() => history.push('/internal/metric-studio/new')}
            startIcon={<Icon name="Add" />}
            variant="text"
          >
            Skapa
          </Button>
        ) : null
      }}
      variant="static"
    >
      <Box sx={{ p: 4 }}>
        <SimpleTable
          columns={[
            { title: 'Mätetal' },
            { title: 'Kategori', width: 180 },
            { title: 'Faktatabell', width: 180 },
            { title: 'Enhet', width: 90 },
            { title: '', width: 110 },
            { title: '', width: 150, align: 'right' }
          ]}
          hover={false}
          loading={fetching && !fetched}
          rows={rows}
        />
      </Box>

      <DeleteDialog
        onClose={() => setToDelete(null)}
        onDelete={(callback) => {
          if (toDelete) {
            tryDeleteDerivedMetric(toDelete.id, () => setToDelete(null))
          }

          callback()
        }}
        open={!!toDelete}
        title={toDelete?.name}
        type="mätetal"
      />
    </PageLayout>
  )
}

export default MetricStudioPage
